import { escape } from 'html-escaper';
import { parseCommentContentToText } from 'zk-editor';
import { isNil } from 'lodash';
import { useRootInstance } from '@/utils/useRootInstance';

const IGNORE_TRIGGER_NAMES = ['export', 'import', 'importComment', 'becomeOrgMember', 'jonOrg', 'joinProject', 'inviteToProject', 'quiteProject', 'quiteToProject', 'projectOwnerTransfer', 'storageWarn', 'storageOverWarn', 'memberWarn', 'actionNumWarn', 'memberOverWarn', 'actionNumOverWarn', 'memberCannotJoin', 'countFavour', 'newMemberProjectInvite', 'reduceRightVersionChangeRole', 'orgOwnerTransfer', 'changeToProjectCollaborator', 'quiteOrg', 'aiCreditNum', 'aiPersonalCreditNum', 'dashboardDeleteJoinSharedNotify', 'joinAllProjects', 'quiteAllProjects', 'jobExecute', 'jobDeadline', 'quiteAllProjectsForManager', 'joinAllProjectsForManager'];
const IGNORE_ACTION_NAMES = ['becomeOrgMember', 'joinProject', 'inviteToProject', 'quiteProject', 'quiteToProject', 'projectOwnerTransfer', 'triggered', 'jonOrg', 'export', 'import', 'importComment', 'storageWarn', 'storageOverWarn', 'memberWarn', 'actionNumWarn', 'memberOverWarn', 'actionNumOverWarn', 'memberCannotJoin', 'countFavour', 'newMemberProjectInvite', 'reduceRightVersionChangeRole', 'orgOwnerTransfer', 'changeToProjectCollaborator', 'quiteOrg', 'aiCreditNum', 'aiPersonalCreditNum', 'dashboardDeleteJoinSharedNotify', 'joinAllProjects', 'quiteAllProjects', 'jobExecute', 'jobDeadline', 'quiteAllProjectsForManager', 'joinAllProjectsForManager'];
const HTML_CONTENT_NAMES = ['joinProject', 'inviteToProject', 'jonOrg', 'quiteProject', 'projectOwnerTransfer', 'quiteToProject', 'becomeOrgMember', 'reply', 'jiraExportSuccess', 'jiraExportError', 'countFavour', 'newMemberProjectInvite', 'orgOwnerTransfer', 'changeToProjectCollaborator', 'quiteOrg', 'dashboardDeleteJoinSharedNotify', 'joinAllProjects', 'quiteAllProjects', 'quiteAllProjectsForManager', 'joinAllProjectsForManager'];

export const actionI18n = (data, i18n) => {
  switch (data.action) {
    case 'create':
      return i18n.t('issue.Create');
    case 'delete':
      return i18n.t('issue.delete');
    case 'reply':
      return i18n.t('issue.Reply');
    default:
      return i18n.t('issue.Update');
  }
};

export const isHtml = (data) => {
  return HTML_CONTENT_NAMES.includes(data.action);
};

export const getAction = (data, i18n) => {
  const { action, content } = data;
  if (action === 'jiraExportSuccess' || action === 'jiraExportError') {
    return '';
  }
  if (action === 'triggered' || action === 'commentTriggered') {
    return i18n.t('message.commentTriggered');
  }
  if (action === 'create' || action === 'delete') {
    return i18n.t('message.action', { action: actionI18n(data, i18n) });
  }
  if (action === 'at') {
    return i18n.t('message.at_action_to');
  }
  if (action === 'reply') {
    return i18n.t('message.action', { action: actionI18n(data, i18n) }) + ' ' + i18n.t('message.commentToMe');
  }
  if (action === 'dashboardShareNotify' || action === 'dashboardCancelShareNotify') {
    return data.receiverName;
  }

  if (action === 'restore') {
    return i18n.t('message.action.recovered');
  }

  if (IGNORE_ACTION_NAMES.includes(action)) {
    return null;
  }
  if (content) {
    return i18n.t('message.action_to', { action: actionI18n(data, i18n), content });
  }
  return i18n.t('message.action', { action: actionI18n(data, i18n) });
};

export const getTrigger = (data) => {
  if (data.action === 'jiraExportSuccess' || data.action === 'jiraExportError') {
    return '';
  }
  // eslint-disable-next-line
  return IGNORE_TRIGGER_NAMES.includes(data.action) ? null : data.trigerName;
};

export const getContent = (data, supportHtml = true) => {
  const textContent = data.plainReceiverName;
  const htmlContent = data.receiverName;
  if (!textContent && !htmlContent) {
    return '';
  }

  if (!supportHtml) {
    if (textContent) {
      return escape(textContent.replace(new RegExp('\\n', 'g'), ' '));
    } if (htmlContent) {
      const instance = useRootInstance();
      return escape(parseCommentContentToText(htmlContent, `[${instance.$i18n.t('editor.codeBlock')}]`).replace(new RegExp('\\n', 'g'), ' '));
    }
  }

  if (supportHtml && isHtml(data)) {
    return parseCommentIconContent(htmlContent);
  }
  const tempEle = document.createElement('div');
  tempEle.innerHTML = htmlContent;
  const text = tempEle.textContent || tempEle.innerText;
  return supportHtml ? parseCommentIconContent(escape(text)) : text;
};

export const parseCommentContent = (data) => {
  const textContent = data.plainReceiverName;
  const htmlContent = data.receiverName;
  if (!textContent && !htmlContent) {
    return '';
  }

  if (textContent) {
    return parseCommentIconContent(escape(textContent.replace(new RegExp('\\n', 'g'), ' ')));
  } if (htmlContent) {
    const instance = useRootInstance();
    return parseCommentIconContent(escape(parseCommentContentToText(htmlContent, `[${instance.$i18n.t('editor.codeBlock')}]`).replace(new RegExp('\\n', 'g'), ' ')));
  }
};

// 处理自定义图标 [ok] => <em class="liga-OK liga-icon"></em>
const iconRegex = new RegExp(
  `(${['[ok]', '[up]'].join('|')})`.replaceAll('[', '\\[').replaceAll(']', '\\]')
);

const iconMap = {
  '[ok]': 'liga-OK'
};
const parseCommentIconContent = (data) => {
  if (data.includes('[') && data.includes(']')) {
    const texts = data.split(iconRegex);
    const result = [];

    texts.forEach((text) => {
      if (text.match(iconRegex)) {
        result.push(`<em style="color: var(--state-waring-icon)" class="liga-icon ${iconMap[text]}"></em>`);
      } else {
        result.push(text);
      }
    });
    return result.join('');
  }
  return data;
};

export const repliedComment = (data, i18n) => {
  if (data.beRepliedCommentStatus === 'deleted') {
    return i18n.t('message.comment.deleted');
  }
  if (isNil(data.beRepliedComment)) {
    return data.beRepliedComment;
  }
  return parseCommentContentToText(data.beRepliedComment, `[${i18n.t('editor.codeBlock')}]`);
};

export const getShareLength = (data) => {
  const length = JSON.parse(data?.extend).reduce((pre, next) => {
    return pre + next.name.length;
  }, 0);
  return length > 76 ? 1 : 0;
};
